import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { Settings } from "lucide-react";

// Helper function to convert 24-hour time to 12-hour AM/PM format
const formatTime12Hour = (time24: string): string => {
  const [hours, minutes] = time24.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight
  return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
};

interface EmployeeAssignment {
  _id: Id<"locationEmployeeAssignments">;
  employeeId: Id<"employees">;
  employeeName: string;
  employeeEmail: string;
  employeePhone: string;
  position: string;
  shift: string;
  shiftId: string; // The shift ID for editing
  assignedAt: number;
}

interface EditEmployeeAssignmentSheetProps {
  isOpen: boolean;
  onClose: () => void;
  assignment: EmployeeAssignment | null;
  onAssignmentUpdated?: () => void;
}

export function EditEmployeeAssignmentSheet({
  isOpen,
  onClose,
  assignment,
  onAssignmentUpdated,
}: EditEmployeeAssignmentSheetProps) {
  const [shift, setShift] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Fetch full employee details
  const employee = useQuery(
    api.employees.getEmployeeById,
    assignment ? { id: assignment.employeeId } : "skip"
  );

  // Fetch enabled shifts from the organization's configuration
  const shifts = useQuery(api.shifts.listEnabled) ?? [];

  // Update assignment mutation
  const updateAssignment = useMutation(api.serviceLocations.updateEmployeeAssignment);

  // Initialize form data when assignment changes
  useEffect(() => {
    if (assignment) {
      setShift(assignment.shiftId); // Use shiftId for the Select component
    }
  }, [assignment]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!assignment) return;

    if (!shift.trim()) {
      toast({
        title: "Error",
        description: "Please select a shift",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await updateAssignment({
        assignmentId: assignment._id,
        position: assignment.position, // Keep the existing position
        shift: shift.trim(),
      });

      toast({
        title: "Success!",
        description: "Assignment updated successfully",
      });

      // Call the callback to refresh data
      if (onAssignmentUpdated) {
        onAssignmentUpdated();
      }

      onClose();
    } catch (error) {
      console.error("Error updating assignment:", error);
      toast({
        title: "Error",
        description: "Failed to update assignment",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when sheet closes
  const handleClose = () => {
    if (assignment) {
      setShift(assignment.shiftId); // Use shiftId for the Select component
    }
    onClose();
  };

  if (!assignment) return null;

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Edit Assignment</SheetTitle>
          <SheetDescription>
            Update assignment details for {assignment.employeeName}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <form onSubmit={handleSubmit} className="space-y-6 px-2 py-6">
            {/* Employee Information (Read-only) */}
            <div className="space-y-3 rounded-lg bg-gray-50 p-4">
              <h3 className="font-medium text-gray-900">Employee Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-600">Name:</span>
                  <p>{assignment.employeeName}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Email:</span>
                  <p className="text-blue-600">{assignment.employeeEmail}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Phone:</span>
                  <p className="text-blue-600">{assignment.employeePhone}</p>
                </div>
                {employee?.position && (
                  <div>
                    <span className="font-medium text-gray-600">Position:</span>
                    <p>{employee.position}</p>
                  </div>
                )}
              </div>
              <div className="text-sm">
                <span className="font-medium text-gray-600">Assigned:</span>
                <span className="ml-2">{formatDate(assignment.assignedAt)}</span>
              </div>
            </div>

            {/* Position (Read-only) */}
            <div className="space-y-2">
              <Label htmlFor="position">Position</Label>
              <Input id="position" value={assignment.position} disabled className="bg-gray-50" />
              <p className="text-sm text-gray-500">
                Position is from the employee's record and cannot be changed here
              </p>
            </div>

            {/* Shift */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="shift">
                  Shift <span className="text-red-500">*</span>
                </Label>
                {shifts && shifts.length === 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-auto px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                    onClick={() => {
                      onClose();
                      navigate("/settings?tab=shifts");
                    }}
                  >
                    <Settings className="mr-1 h-3 w-3" />
                    Configure Shifts
                  </Button>
                )}
              </div>
              <Select value={shifts && shifts.length === 0 ? "" : shift} onValueChange={setShift}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      shifts === undefined
                        ? "Loading shifts..."
                        : shifts.length === 0
                          ? "No shifts configured"
                          : "Select a shift"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {shifts && shifts.length > 0 ? (
                    shifts.map((shiftOption: any) => {
                      // Format the display text based on whether the shift has a time range
                      const displayText =
                        shiftOption.startTime && shiftOption.endTime
                          ? `${shiftOption.name} (${formatTime12Hour(shiftOption.startTime)} - ${formatTime12Hour(shiftOption.endTime)})`
                          : shiftOption.name;

                      return (
                        <SelectItem key={shiftOption._id} value={shiftOption._id}>
                          {displayText}
                        </SelectItem>
                      );
                    })
                  ) : (
                    <SelectItem value="no-shifts" disabled>
                      No shifts available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Updating..." : "Update Assignment"}
              </Button>
            </div>
          </form>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
